import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../../services/api';
import { useTopologyBase } from './topologyApi';

/**
 * Rebuild do mapa pelo CLIENTE, com a janela de 15 minutos que a API impõe.
 *
 * O admin reconstrói com `GET ?force=true` e sem trava — é operação de suporte.
 * Para o cliente existe rota própria (`POST /topology/rebuild`) porque o custo é
 * real: 8,6s de CPU e 171 MB na maior empresa medida. A trava vive no servidor,
 * num `SET NX EX` atômico; aqui é só espelho, para o botão não mentir.
 *
 * `ativo` desliga o hook no modo admin em vez de o chamador chamá-lo
 * condicionalmente — hook condicional quebra a ordem que o React espera.
 */
export default function useRebuildCliente(ativo) {
  const base = useTopologyBase();
  const [faltam, setFaltam] = useState(0);
  const [rodando, setRodando] = useState(false);
  const [erro, setErro] = useState(null);
  const timer = useRef(null);

  const lerStatus = useCallback(async () => {
    if (!ativo || !base) return;
    try {
      const r = await api.get(`${base}/rebuild/status`);
      setFaltam(r.data?.liberado ? 0 : Number(r.data?.faltam_segundos || 0));
    } catch {
      // status é conveniência: se falhar, o botão continua e quem decide é o
      // servidor, que devolve 429 com o tempo restante.
      setFaltam(0);
    }
  }, [ativo, base]);

  useEffect(() => { lerStatus(); }, [lerStatus]);

  // Conta regressiva local. Bater no servidor a cada segundo só para desenhar um
  // relógio seria uma requisição por segundo por aba aberta.
  useEffect(() => {
    if (!ativo || faltam <= 0) return undefined;
    timer.current = setInterval(() => setFaltam((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(timer.current);
  }, [ativo, faltam]);

  const rebuild = useCallback(async () => {
    if (!base) return null;
    setRodando(true);
    setErro(null);
    try {
      const r = await api.post(`${base}/rebuild`, {});
      setFaltam(Number(r.data?.proximo_em_segundos || 0));
      return r.data;
    } catch (e) {
      const st = e?.response?.status;
      if (st === 429) {
        // A recusa traz o tempo que falta; reler o status evita o botão
        // reabilitar antes da hora por causa de relógio local.
        setErro(e?.response?.data?.detail || 'Reconstruído há pouco.');
        lerStatus();
      } else {
        setErro(e?.response?.data?.detail || 'Falha ao reconstruir o mapa.');
      }
      return null;
    } finally {
      setRodando(false);
    }
  }, [base, lerStatus]);

  return { faltam, rodando, erro, rebuild, limparErro: () => setErro(null) };
}

/** "3m 20s" — o botão precisa dizer quanto falta, não só que está travado. */
export function formataEspera(segundos) {
  const s = Math.max(0, Math.floor(segundos));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}
